import React, { FC, useCallback, useState } from 'react';
import useDispatch from 'src/common/hooks/useAppDispatch';
import { useHistory, useLocation } from 'react-router-dom';
import Icon from 'src/common/components/IconDS22';
import { trackEvent } from 'src/common/tracking';
import getClassName from 'src/common/utils/getClassName';
import rffSubmitResponse from 'src/common/utils/rffSubmitResponse';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import type { ImmutableTrip, TripStateState } from 'src/types/trips/Trips';
import GA_EVENTS from 'src/common/components/UpdateAvailability/constants';
import { PATHS } from '../../../common/constants';
import { open, close } from '../../../common/ducks/zippy';
import { getMessagesCall } from '../../ducks/messages';
import { preserveSearch } from '../../../common/utils/routing';
import { CTAButton } from '../CTA';
import Dropdown from '../../../common/components/Dropdown';
import Modal from '../../../common/components/Modal';
import TripActionForm from '../forms/TripAction';
import {
  getActions,
  getRoleSpecificActions,
  SEND_MESSAGE_ACTION_NAME,
  CANCEL_RESERVATION_ACTION_NAME,
} from './actions';
import s from './TripMenu.module.scss';

import { userIsRenter } from '../../helpers';

type TripMenuProps = {
  trip: ImmutableTrip;
  cancellationDropdownOptions?: DropdownOption[];
};

const TripMenu: FC<TripMenuProps> = ({
  cancellationDropdownOptions = [],
  trip,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const tripState: TripStateState = trip.getIn(['state', 'state']);
  const tripId = trip.get('pk');
  const actions = getRoleSpecificActions(trip);
  const activeActions = getActions(tripState).map(action => action.name);
  const [offerAnotherDate, setOfferAnotherDate] = useState(false);

  const closeBehavior = useCallback((modalName: string) => {
    dispatch(close(modalName));
    setOfferAnotherDate(false);
  }, [dispatch]);

  return (
    <Dropdown className={s.root}>
      {({ close: closeTripMenu, toggle, isOpen }) => (
        <>
          <CTAButton
            type="button"
            onClick={toggle}
            classNameModifier="gray options secondary"
            data-test="trip-menu"
          >
            Options
            <Icon
              id="caret-up"
            />
          </CTAButton>
          <ul className={getClassName(s, 'content', isOpen ? 'open' : '')}>
            {actions.map(action => {
              const Component = action.component;
              const modalName = `tripAction-${action.name}`;
              const destinationPath = `${PATHS.INBOX}${tripId}/${action.destination || ''}`;
              const disabled = !activeActions.includes(action.name) || action?.isDisabled?.(trip);
              const disabledModifier = disabled ? 'disabled' : '';

              return (
                <li
                  className={s.item}
                  key={action.name}
                >
                  <Component
                    to={action.url || preserveSearch(destinationPath, location)}
                    onClick={() => {
                      if (action.call) {
                        dispatch(open(modalName));
                      } else if (action.onClick) {
                        action.onClick(dispatch, trip, { history, location });
                      }
                      closeTripMenu();
                    }}
                    disabled={!!disabled}
                    className={getClassName(s, 'action', disabledModifier)}
                    {...((action.component?.displayName || action.component?.name) === 'NavLink'
                      ? { activeClassName: s.action_active }
                      : {})}
                  >
                    <Icon
                      id={action.icon}
                      size="l"
                    />
                    <span className={s.text}>
                      <strong className={s.title}>
                        {action.title}
                      </strong>
                      {action.shortDescription && (
                        <em className={s.description}>
                          {action.shortDescription}
                        </em>
                      )}
                    </span>
                  </Component>
                  {action.call && (
                    <Modal
                      closeModal={() => {
                        closeBehavior(modalName);
                        if (offerAnotherDate) {
                          trackEvent(GA_EVENTS.OFFER_ANOTHER_DATE_MODAL.CLOSE);
                        }
                      }}
                      name={modalName}
                      label={`${action.title} Form`}
                      extraClassName="ignore-onclickoutside"
                    >
                      <TripActionForm
                        content={{
                          action: action.name,
                          title: action.title,
                          description: action.longDescription || action.shortDescription,
                          submitText: action.submitText,
                          cancelText: action.cancelText,
                          placeholder: action.placeholder,
                        }}
                        closeBehavior={() => closeBehavior(modalName)}
                        fieldName={action.name === SEND_MESSAGE_ACTION_NAME
                          ? 'content'
                          : 'message'}
                        messageLabel={action.messageLabel}
                        withMessageCharacterCounter={action.withMessageCharacterCounter}
                        withDropdownField={!!(action.dropdownFieldName
                          && cancellationDropdownOptions.length > 0)}
                        dropdownFieldName={action.dropdownFieldName}
                        dropdownOptions={cancellationDropdownOptions}
                        dropdownPlaceholder={action.dropdownPlaceholder}
                        onSubmit={values => dispatch(
                          action.call(
                            tripId,
                            {
                              ...values,
                              ...(action.name === SEND_MESSAGE_ACTION_NAME
                                // add this field into values for send message call
                                ? { communications: ['email'] }
                                : {}
                              ),
                            },
                          ),
                        )
                          .then(() => {
                            dispatch(getMessagesCall(tripId));

                            if (action.name === CANCEL_RESERVATION_ACTION_NAME) {
                              trackEvent('Canceled', {
                                event_category: 'Booking',
                                event_label: userIsRenter(trip) ? 'Renter' : 'Owner',
                              });
                            }

                            history.replace(
                              preserveSearch(destinationPath, location),
                            );
                            closeBehavior(modalName);
                          })
                          .catch(rffSubmitResponse())}
                        withMessageField={action.withMessageField}
                        offerAnotherDate={offerAnotherDate}
                        setOfferAnotherDate={setOfferAnotherDate}
                        trip={trip}
                      />
                    </Modal>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Dropdown>
  );
};

export default TripMenu;
